import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AgentRunRow } from '@/components/px/AgentRunRow'
import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { OpenTaskBanner } from '@/components/px/OpenTaskBanner'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ANCHORS,
  DENSITY_NOTE,
  EXEMPLAR_BLOCK,
  EXEMPLAR_KPIS,
  HREFS,
  MAINTAIN_PAGE,
  PLAN,
  RUN_LOG,
  SCAN,
  SCAN_DETAIL,
  SCAN_VERDICTS,
  SCOUT_RUNS,
  SIGNALS,
  SIGNALS_TABLE,
  TRACE,
  TRUST,
  type Fragment,
  type RunKind,
} from '@/demo/content/maintain'
import { heroProject } from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * 3.9 Maintain — AMS, signals and the next upgrade.
 *
 * Two registers, labelled as such (the hero project is in Build and has no
 * AMS history to show): Van Dijk's PLAN at the top, then the live exemplar —
 * Bergman Winkels, the seeded maintain-phase client — carrying the operating
 * figures. The page never lets a projection wear a live figure's clothes.
 *
 * Colour follows the locked contract: mint for what PX proposes (the signals
 * table, the draft quote, the send action), aubergine for decided/dark
 * furniture (the scout panel, the "you are here" stage). The trust model is a
 * visible statement beside the agent-draft figure, not a footnote: an agent
 * drafts, a named person sends.
 */

const RUN_TONE: Record<RunKind, StatusTone> = {
  'awaiting-send': 'review',
  superseded: 'neutral',
}

/**
 * Inline nodes for a sentence whose link targets live in the content module.
 * Returns nodes rather than a block, so it can also be dropped inside
 * components that render their own paragraph (DensityNote).
 */
const sentenceNodes = (parts: readonly Fragment[], linkClassName?: string): ReactNode[] =>
  parts.map((part, i) =>
    part.to ? (
      <Link
        key={`${part.text}-${i}`}
        to={part.to}
        className={cn('font-medium hover:underline', linkClassName ?? 'text-accent-foreground')}
      >
        {part.text}
      </Link>
    ) : (
      <span key={`${part.text}-${i}`}>{part.text}</span>
    )
  )

function Sentence({
  parts,
  className,
  linkClassName,
}: {
  parts: readonly Fragment[]
  className?: string
  linkClassName?: string
}) {
  return <p className={className}>{sentenceNodes(parts, linkClassName)}</p>
}

export default function MaintainPage() {
  const project = heroProject()
  const nav = useMemo(() => projectNav(project), [project])

  return (
    <AppShell
      moduleLabel={MAINTAIN_PAGE.moduleLabel}
      breadcrumb={project.name}
      contextPill={MAINTAIN_PAGE.contextPill}
      nav={nav}
    >
      {/* PX-W6.6 (AC-1.6): this surface renders demo content and had no
          badge. Never hide a surface — badge it. */}
      <div className="mb-3">
        <ConceptBadge />
      </div>
      <header className="max-w-4xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{MAINTAIN_PAGE.title}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{MAINTAIN_PAGE.standfirst}</p>
      </header>

      <PhaseRibbon current={MAINTAIN_PAGE.phase} className="mt-4" />

      {/* ── 1 · The plan for the hero project ───────────────────────────── */}
      <section
        aria-labelledby="plan-heading"
        className="mt-5 rounded-lg border border-border bg-card px-4 py-3"
      >
        <SectionLabel>{PLAN.label}</SectionLabel>
        <h2 id="plan-heading" className="sr-only">
          {PLAN.label}
        </h2>
        <Sentence parts={PLAN.sentence} className="mt-1 max-w-3xl text-sm text-foreground" />
        <p className="mt-1.5 max-w-3xl text-xs text-muted-foreground">{PLAN.meta}</p>
      </section>

      {/* ── 2 · The live exemplar ───────────────────────────────────────── */}
      <section id={ANCHORS.exemplar} aria-labelledby="exemplar-heading" className="mt-7">
        <SectionLabel>{EXEMPLAR_BLOCK.label}</SectionLabel>
        <h2 id="exemplar-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {EXEMPLAR_BLOCK.heading}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {EXEMPLAR_BLOCK.meta}
          {' · '}
          <Link to={HREFS.clients} className="font-medium text-accent-foreground hover:underline">
            {EXEMPLAR_BLOCK.clientsLinkLabel}
          </Link>
        </p>

        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {EXEMPLAR_KPIS.map((kpi) => (
            <KpiCard key={kpi.key} label={kpi.label} figure={kpi.figure} qualifier={kpi.qualifier} />
          ))}
        </div>

        {/* The trust model, at figure weight beside the figure it qualifies. */}
        <div className="mt-3 flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 sm:flex-row sm:items-start">
          <StatusPill tone="decided" className="shrink-0">
            {TRUST.pill}
          </StatusPill>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{TRUST.headline}</p>
            <p className="mt-0.5 max-w-3xl text-xs text-muted-foreground">{TRUST.detail}</p>
          </div>
        </div>
      </section>

      {/* ── 3 · Signals → next tasks (proposals: mint) ──────────────────── */}
      <section
        id={ANCHORS.signals}
        aria-labelledby="signals-heading"
        className="mt-7 rounded-lg border border-border bg-accent"
      >
        <div className="flex flex-wrap items-baseline gap-2 border-b border-mint/30 px-4 py-3">
          <div className="min-w-0 flex-1">
            <SectionLabel>{SIGNALS_TABLE.label}</SectionLabel>
            <h2 id="signals-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
              {SIGNALS_TABLE.heading}
            </h2>
          </div>
          <StatusPill tone="proposed" className="border border-mint/40">
            {SIGNALS_TABLE.proposalTag}
          </StatusPill>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-mint/30 hover:bg-transparent">
              <TableHead>{SIGNALS_TABLE.columns.signal}</TableHead>
              <TableHead>{SIGNALS_TABLE.columns.evidence}</TableHead>
              <TableHead>{SIGNALS_TABLE.columns.proposed}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SIGNALS.map((signal) => (
              <TableRow key={signal.key} className="border-mint/30 hover:bg-mint/10">
                <TableCell className="px-4 whitespace-normal text-[13px] font-medium text-foreground">
                  {signal.signal}
                </TableCell>
                <TableCell className="whitespace-normal">
                  <span className="flex flex-col items-start gap-1">
                    <Badge variant="outline" className="border-mint/40 bg-card">
                      {signal.evidenceKind}
                    </Badge>
                    <span className="text-[13px] text-ink-soft">{signal.evidence}</span>
                  </span>
                </TableCell>
                <TableCell className="px-4 whitespace-normal text-[13px] font-medium text-accent-foreground">
                  {SIGNALS_TABLE.proposedLine(signal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="border-t border-mint/30 px-4 py-2.5">
          <Sentence parts={SIGNALS_TABLE.caption} className="text-xs text-muted-foreground" />
          <p className="mt-0.5 text-xs text-muted-foreground">{SIGNALS_TABLE.total}</p>
        </div>
      </section>

      {/* ── 4 · The upgrade scout ───────────────────────────────────────── */}
      <section id={ANCHORS.scan} aria-labelledby="scan-heading" className="mt-6">
        <h2 id="scan-heading" className="sr-only">
          {SCAN.eyebrow}
        </h2>
        <OpenTaskBanner eyebrow={SCAN.eyebrow} title={SCAN.title} meta={SCAN.meta}>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="flex flex-wrap items-center gap-2 sm:pt-2">
              <StatusPill tone="proposed">{SCAN.quoteChip}</StatusPill>
              <StatusPill tone="neutral">{SCAN.daysChip}</StatusPill>
            </div>
            <div className="flex flex-col gap-1.5 sm:ml-auto sm:items-end">
              <Button asChild variant="accent" size="lg">
                <Link to={SCAN.ctaHref} aria-describedby="scan-consequence">
                  {SCAN.ctaLabel}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
              <p id="scan-consequence" className="max-w-md text-xs text-white/80 sm:text-right">
                {SCAN.consequence}
              </p>
            </div>
          </div>
        </OpenTaskBanner>

        <Sentence
          parts={SCAN.scope}
          className="mt-2 max-w-3xl text-xs text-muted-foreground"
        />
      </section>

      {/* ── The scan, in detail, and the runs that produced it ──────────── */}
      <section
        id={ANCHORS.scanDetail}
        aria-labelledby="scan-detail-heading"
        className="mt-6 rounded-lg border border-border bg-card"
      >
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{SCAN_DETAIL.label}</SectionLabel>
          <h2 id="scan-detail-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {SCAN_DETAIL.heading}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {SCAN_DETAIL.agentLine}
            {' · '}
            <Link to={SCAN_DETAIL.agentHref} className="font-medium text-accent-foreground hover:underline">
              {SCAN_DETAIL.agentLinkLabel}
            </Link>
          </p>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{SCAN_DETAIL.columns.verdict}</TableHead>
              <TableHead>{SCAN_DETAIL.columns.modules}</TableHead>
              <TableHead>{SCAN_DETAIL.columns.meaning}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SCAN_VERDICTS.map((verdict) => (
              <TableRow key={verdict.key}>
                <TableCell className="px-4 text-[13px] font-medium text-foreground">
                  {verdict.verdict}
                </TableCell>
                <TableCell className="v-figure text-[13px] tabular-nums text-foreground">
                  {verdict.modules}
                </TableCell>
                <TableCell className="whitespace-normal text-[13px] text-ink-soft">
                  {verdict.meaning}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="border-t border-border px-4 py-3">
          <SectionLabel>{RUN_LOG.label}</SectionLabel>
          <h3 className="mt-0.5 text-sm font-semibold text-foreground">{RUN_LOG.heading}</h3>
          <div className="mt-1">
            {SCOUT_RUNS.map((run) => (
              <AgentRunRow
                key={run.key}
                tone={RUN_TONE[run.kind]}
                label={run.label}
                text={run.text}
                time={run.time}
                runHref={run.runHref}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 5 · The trace line ──────────────────────────────────────────── */}
      <section
        id={ANCHORS.trace}
        aria-labelledby="trace-heading"
        className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
      >
        <SectionLabel>{TRACE.label}</SectionLabel>
        <h2 id="trace-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {TRACE.heading}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">{TRACE.lead}</p>

        <ol className="mt-3 flex flex-wrap items-stretch gap-2">
          {TRACE.chain.map((stage, i) => {
            const here = stage.to === TRACE.hereHref
            return (
              <li key={stage.key} className="flex items-stretch gap-2">
                {i > 0 && (
                  <ArrowRight className="h-4 w-4 shrink-0 self-center text-muted-foreground" aria-hidden />
                )}
                {here ? (
                  <div className="rounded-md bg-primary px-3 py-2 text-primary-foreground">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/70">
                      {stage.stage}
                    </div>
                    <div className="text-[13px] font-medium">{stage.text}</div>
                    <div className="text-[11px] text-white/70">{TRACE.hereLabel}</div>
                  </div>
                ) : (
                  <Link
                    to={stage.to}
                    className="rounded-md border border-border px-3 py-2 hover:bg-muted"
                  >
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      {stage.stage}
                    </span>
                    <span className="block text-[13px] font-medium text-accent-foreground">
                      {stage.text}
                    </span>
                  </Link>
                )}
              </li>
            )
          })}
        </ol>

        <blockquote className="mt-4 border-l-2 border-mint pl-3 font-serif text-base text-foreground">
          {TRACE.quote}
        </blockquote>
        <p className="mt-2 max-w-3xl text-xs text-muted-foreground">{TRACE.closing}</p>
      </section>

      <DensityNote>{sentenceNodes(DENSITY_NOTE)}</DensityNote>
    </AppShell>
  )
}
